/**
 * CalibrationDrawer — debug инструменты для cloud mask calibration.
 * Открывается из SatellitesPanel через "⚙ Калибровка".
 *
 * Spec v1.3: эти toggle'ы НЕ для обычного юзера — они нужны агроному, чтобы
 * проверить когда NDVI выглядит странно (облако? тень? ложный SCL class?).
 *
 * Содержит:
 *  - Toggle "Снимок (TCI)" — true-color overlay
 *  - Toggle "Маска облачности" — SCL classes 8-11 как красная маска
 *  - (Future) SCL statistics, per-tenant tuning slider
 */
import { Camera, Cloud, Info } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMapStore } from '@/lib/map/useMapStore';

export function CalibrationDrawer() {
  const open = useMapStore((s) => s.calibrationOpen);
  const setOpen = useMapStore((s) => s.setCalibrationOpen);
  const showTCI = useMapStore((s) => s.showTCI);
  const showCloudMask = useMapStore((s) => s.showCloudMask);
  const setShowTCI = useMapStore((s) => s.setShowTCI);
  const setShowCloudMask = useMapStore((s) => s.setShowCloudMask);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-[360px] sm:max-w-[360px]">
        <SheetHeader>
          <SheetTitle>Калибровка снимков</SheetTitle>
          <SheetDescription>
            Debug-инструменты для проверки качества cloud mask и снимков.
            Не нужны при обычной работе.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <Camera className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <Label
                  htmlFor="cal-tci"
                  className="text-sm font-medium cursor-pointer"
                >
                  Снимок (TCI)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Натуральные цвета — что увидел спутник
                </p>
              </div>
            </div>
            <Switch
              id="cal-tci"
              checked={showTCI}
              onCheckedChange={setShowTCI}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <Cloud className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <Label
                  htmlFor="cal-cloud"
                  className="text-sm font-medium cursor-pointer"
                >
                  Маска облачности
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  SCL classes 8–11 (cloud / shadow) поверх карты
                </p>
              </div>
            </div>
            <Switch
              id="cal-cloud"
              checked={showCloudMask}
              onCheckedChange={setShowCloudMask}
            />
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs leading-relaxed">
              Если NDVI выглядит подозрительно (вдруг "красное" поле, которое
              неделю назад было зелёное) — включите эти слои чтобы проверить
              облако / тень. SCL-статистика и per-tenant настройка маски —
              в следующем релизе.
            </AlertDescription>
          </Alert>
        </div>
      </SheetContent>
    </Sheet>
  );
}
